import { Button, Card, Form, Input, Switch, Typography } from 'antd';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ToastVariant } from '../../../../components/Toast/toast.model';
import { toastService } from '../../../../components/Toast/toast.service';
import { firestore } from '../../../../firebase/firebase';
import { FirestoreCollection } from '../../../../firebase/firebase.models';
import { useAuth } from '../../../Auth/AuthContext';

const { Text } = Typography;

export const RealEstateAgentSection: React.FC = () => {
  const [componentDisabled, setComponentDisabled] = useState<boolean>(true);
  const { currentUser } = useAuth();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState<{
    isRealEstateAgent?: boolean;
    professionalLicenseNumber?: string;
  }>({});

  useEffect(() => {
    if (!currentUser?.uid) return;

    const userDoc = firestore.collection(FirestoreCollection.USERS).doc(currentUser.uid);
    userDoc.get().then((snapshot) => {
      const data = snapshot.data();
      if (data) {
        const agentData = {
          isRealEstateAgent: data.isRealEstateAgent || false,
          professionalLicenseNumber: data.professionalLicenseNumber || '',
        };
        setInitialData(agentData);
        form.setFieldsValue(agentData);
      }
    });
  }, [currentUser?.uid, form]);

  const onFinish = async (values: any) => {
    if (!currentUser?.uid) return;

    setLoading(true);
    try {
      await firestore
        .collection(FirestoreCollection.USERS)
        .doc(currentUser.uid)
        .update({
          isRealEstateAgent: values.isRealEstateAgent,
          professionalLicenseNumber: values.isRealEstateAgent ? values.professionalLicenseNumber : null,
        });

      toastService.show('Ustawienia pełnomocnika zostały zapisane', t('SUCCESS'), {
        variant: ToastVariant.SUCCESS,
      });
      setComponentDisabled(true);
      setInitialData(values);
    } catch (error) {
      console.error('Error updating agent settings:', error);
      toastService.show('Wystąpił błąd podczas zapisywania ustawień', t('ERROR'), {
        variant: ToastVariant.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const onReset = () => {
    form.setFieldsValue(initialData);
    setComponentDisabled(true);
  };

  return (
    <Card className="shadow-sm border border-gray-200 rounded-lg">
      <div className="space-y-4">
        <div className="border-b border-gray-100 pb-3">
          <Text className="text-gray-800 text-base font-semibold">Funkcje pełnomocnika</Text>
        </div>

        <Form
          layout="vertical"
          onFinish={onFinish}
          form={form}
          disabled={componentDisabled}
          initialValues={initialData}>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Form.Item name="isRealEstateAgent" valuePropName="checked" className="!mb-0">
                <Switch />
              </Form.Item>
              <div className="flex-1">
                <Text className="text-sm font-medium text-gray-700">Wysyłanie projektów ofert</Text>
                <div className="text-xs text-gray-500 mt-1">Włącz, aby działać jak profesjonalista</div>
              </div>
            </div>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.isRealEstateAgent !== currentValues.isRealEstateAgent
              }>
              {({ getFieldValue }) =>
                getFieldValue('isRealEstateAgent') ? (
                  <div className="space-y-4 mt-4 pl-9">
                    <Form.Item
                      label={<Text className="text-sm font-medium text-gray-700">Nr licencji</Text>}
                      name="professionalLicenseNumber"
                      className="!mb-0">
                      <Input className="text-sm max-w-xs" placeholder="np. 12345" />
                      <div className="text-xs text-gray-500 mt-1">
                        Numer licencji zawodowej pośrednika (opcjonalnie)
                      </div>
                    </Form.Item>

                    <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                      <Text className="text-sm text-gray-800 font-medium block mb-3">
                        Jako agent nieruchomości otrzymasz możliwość:
                      </Text>
                      <ul className="text-sm text-gray-700 space-y-2">
                        <li className="flex items-start">
                          <span className="text-blue-500 mr-2 mt-0.5">•</span>
                          <span>Dodawania ofert w imieniu swoich klientów</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-500 mr-2 mt-0.5">•</span>
                          <span>Generowania umów pośrednictwa</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-500 mr-2 mt-0.5">•</span>
                          <span>Wysyłania profesjonalnych propozycji współpracy</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-500 mr-2 mt-0.5">•</span>
                          <span>Zarządzania portfolio nieruchomości klientów</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                ) : null
              }
            </Form.Item>
          </div>

          {!componentDisabled && (
            <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-gray-100">
              <Button type="primary" htmlType="submit" loading={loading}>
                Zapisz
              </Button>
              <Button htmlType="button" onClick={onReset}>
                Anuluj
              </Button>
            </div>
          )}
        </Form>

        {componentDisabled && (
          <div className="flex justify-end pt-4 border-t border-gray-100">
            <Button type="primary" onClick={() => setComponentDisabled(false)}>
              Edytuj
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};
